import { useEffect } from 'react';

type BreadcrumbItem = {
  name: string;
  url: string;
};

type SEOProps = {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  keywords?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
  locale?: string;
  alternateLocales?: string[];
  breadcrumbs?: BreadcrumbItem[];
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
};

const DEFAULTS = {
  title: "Amanaliya — Contemporary Painter | Original Artworks",
  description:
    "Art for the heart — original paintings by Amanaliya Art, Swiss-French contemporary artist. Available for sale, worldwide shipping, free delivery in Europe.",
  image: 'https://static.lumi.new/87/8702d61b4a30cdd7ae853f8d010d8c15.webp',
  siteName: "Amanaliya Art",
  baseUrl: 'https://www.amanaliya.com',
  twitterHandle: '@amanaliya_art',
  locale: 'en_US',
};

const absoluteUrl = (url?: string): string => {
  if (!url) return DEFAULTS.baseUrl + '/';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return DEFAULTS.baseUrl + url;
  return DEFAULTS.baseUrl + '/' + url;
};

const upsertMeta = (
  selector: string,
  attr: 'name' | 'property',
  key: string,
  value: string,
) => {
  if (!value) return;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const upsertLink = (rel: string, href: string, extraAttrs?: Record<string, string>) => {
  if (!href) return;
  const selectorKey = extraAttrs?.hreflang
    ? `link[rel="${rel}"][hreflang="${extraAttrs.hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selectorKey);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  if (extraAttrs) {
    Object.entries(extraAttrs).forEach(([k, v]) => el!.setAttribute(k, v));
  }
};

const removeJsonLd = () => {
  document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
};

const addJsonLd = (data: object | object[]) => {
  removeJsonLd();
  const items = Array.isArray(data) ? data : [data];
  items.forEach((obj) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(obj);
    document.head.appendChild(script);
  });
};

const buildBreadcrumbJsonLd = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.url),
  })),
});

const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  keywords,
  noIndex = false,
  jsonLd,
  locale,
  alternateLocales,
  breadcrumbs,
  publishedTime,
  modifiedTime,
  author,
}: SEOProps) => {
  useEffect(() => {
    const finalTitle = title
      ? `${title} | ${DEFAULTS.siteName}`
      : DEFAULTS.title;
    const finalDesc = description || DEFAULTS.description;
    const finalImage = image || DEFAULTS.image;
    const finalUrl = url
      ? absoluteUrl(url)
      : (typeof window !== 'undefined' ? window.location.href : DEFAULTS.baseUrl + '/');
    const finalLocale = locale || DEFAULTS.locale;

    // Title
    document.title = finalTitle;

    // Core
    upsertMeta('meta[name="description"]', 'name', 'description', finalDesc);
    // Meta keywords is deprecated and has no effect on modern search engines;
    // any existing tag is actively removed rather than (re)written.
    document.head.querySelector('meta[name="keywords"]')?.remove();
    upsertMeta(
      'meta[name="robots"]',
      'name',
      'robots',
      noIndex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    );
    if (author) upsertMeta('meta[name="author"]', 'name', 'author', author);

    // Open Graph
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', finalTitle);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', finalDesc);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', finalImage);
    upsertMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', finalTitle);
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', finalUrl);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', type);
    upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', DEFAULTS.siteName);
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', finalLocale);
    if (alternateLocales?.length) {
      // Clean existing alternates
      document.head
        .querySelectorAll('meta[property="og:locale:alternate"]')
        .forEach((n) => n.remove());
      alternateLocales.forEach((alt) => {
        const el = document.createElement('meta');
        el.setAttribute('property', 'og:locale:alternate');
        el.setAttribute('content', alt);
        document.head.appendChild(el);
      });
    }

    // Article-specific (only when type === 'article')
    if (type === 'article') {
      if (publishedTime)
        upsertMeta(
          'meta[property="article:published_time"]',
          'property',
          'article:published_time',
          publishedTime,
        );
      if (modifiedTime)
        upsertMeta(
          'meta[property="article:modified_time"]',
          'property',
          'article:modified_time',
          modifiedTime,
        );
      if (author)
        upsertMeta('meta[property="article:author"]', 'property', 'article:author', author);
    }

    // Twitter
    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', finalTitle);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', finalDesc);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', finalImage);
    upsertMeta('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', finalTitle);
    upsertMeta('meta[name="twitter:url"]', 'name', 'twitter:url', finalUrl);
    upsertMeta('meta[name="twitter:site"]', 'name', 'twitter:site', DEFAULTS.twitterHandle);
    upsertMeta('meta[name="twitter:creator"]', 'name', 'twitter:creator', DEFAULTS.twitterHandle);

    // Canonical
    upsertLink('canonical', finalUrl);

    // hreflang (default to en + x-default to the same page)
    upsertLink('alternate', finalUrl, { hreflang: 'en' });
    upsertLink('alternate', finalUrl, { hreflang: 'x-default' });

    // JSON-LD: page-specific + optional breadcrumbs
    const payload: object[] = [];
    if (jsonLd) {
      if (Array.isArray(jsonLd)) payload.push(...jsonLd);
      else payload.push(jsonLd);
    }
    if (breadcrumbs && breadcrumbs.length > 0) {
      payload.push(buildBreadcrumbJsonLd(breadcrumbs));
    }
    if (payload.length > 0) {
      addJsonLd(payload);
    } else {
      removeJsonLd();
    }

    return () => {
      removeJsonLd();
    };
  }, [
    title,
    description,
    image,
    url,
    type,
    keywords,
    noIndex,
    jsonLd,
    locale,
    alternateLocales,
    breadcrumbs,
    publishedTime,
    modifiedTime,
    author,
  ]);

  return null;
};

export default SEO;
